/**
 * BRAND MANAGE PAGE
 * 
 * Dashboard for verified brand owners to manage their brand page.
 * 
 * Features:
 * - Branding: Upload logo and banner
 * - Profile: Edit description, location, social links
 * - Collections: Create and manage official collections
 * - Products: Add products to collections
 * - Announcements: Post news and updates
 * 
 * Access: Only accessible to verified brand owners (claimedBy === currentUser.uid OR ownerEmail === currentUser.email)
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  Image,
  User,
  Package,
  Sparkles,
  Megaphone,
  Save,
  ArrowLeft,
  Upload,
  Trash2,
  Plus,
  X,
  Edit2,
  ExternalLink,
  Instagram,
  Globe,
  MapPin,
  Building2,
  Check,
  AlertCircle,
  Clock,
  HardDrive,
  Tag
} from 'lucide-react';
import { getCurrentUser } from '../../firebase/auth';
import { doc, updateDoc, serverTimestamp, collection, getDocs, deleteDoc, query, orderBy, increment } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { getBrandBySlug, getBrandCollections } from '../../services/brandService';
import type { Brand, BrandCollection } from '../../types/brand';
import BrandCollectionModal from './BrandCollectionModal';
import BrandProductModal from './BrandProductModal';
import BrandToolsTab from './BrandToolsTab';
import { ACCEPTED_IMAGE_TYPES, compressImage } from '../../utils/imageUtils';

// Brand storage limit (500 MB in bytes)
const BRAND_STORAGE_LIMIT = 500 * 1024 * 1024; // 500 MB
const BRAND_STORAGE_WARNING = 400 * 1024 * 1024; // 400 MB - warn at 80%

// Timezone options
const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
];

// Helper to extract photo URL from either string or object format
// Handles: string URLs, { url: '...' } objects
const getPhotoUrl = (photo: string | { url: string } | undefined): string | undefined => {
  if (!photo) return undefined;
  if (typeof photo === 'string') return photo;
  if (typeof photo === 'object' && 'url' in photo) return photo.url;
  return undefined;
};

// Helper to extract photo caption from object format
const getPhotoCaption = (photo: string | { caption?: string } | undefined): string | undefined => {
  if (!photo || typeof photo === 'string') return undefined;
  if (typeof photo === 'object' && 'caption' in photo) return photo.caption;
  return undefined;
};

// Helper to extract photo credit name from object format
const getPhotoCreditName = (photo: string | { creditName?: string } | undefined): string | undefined => {
  if (!photo || typeof photo === 'string') return undefined;
  if (typeof photo === 'object' && 'creditName' in photo) return photo.creditName;
  return undefined;
};

const INTERNATIONAL_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris / Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Photo with credit information (matches BrandProductModal)
interface ProductPhoto {
  url: string;
  caption?: string;
  creditName?: string;
  creditUrl?: string;
}

// Product type
interface BrandProduct {
  id: string;
  name: string;
  description?: string | null;
  colorDescription?: string | null;
  category: string;
  photos: ProductPhoto[];
  collectionId?: string | null;
  collectionName?: string | null;
  status: 'available' | 'coming_soon' | 'sold_out' | 'discontinued';
  price?: number | null;
  sku?: string | null;
  tags?: string[];
  createdAt?: any;
  updatedAt?: any;
}

// Tab types
type ManageTab = 'branding' | 'profile' | 'collections' | 'products' | 'brand-tools' | 'announcements';

// Announcement type
interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'news' | 'release' | 'sale' | 'event';
  createdAt: Date;
  updatedAt?: Date;
  isPinned?: boolean;
}

export default function BrandManagePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const currentUser = getCurrentUser();

  // State
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Tab defaults to Branding but honors a `?tab=polishes` (etc.) query
  // param so deep links from the polish detail page can land directly
  // on the right tab. Any value not in the enum falls through to the
  // default via the type check.
  const [activeTab, setActiveTab] = useState<ManageTab>(() => {
    const t = searchParams.get('tab');
    const allowed: ManageTab[] = ['branding', 'profile', 'collections', 'products', 'brand-tools', 'announcements'];
    return (allowed as string[]).includes(t ?? '') ? (t as ManageTab) : 'branding';
  });
  
  // Branding state
  const [logo, setLogo] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  
  // Profile state
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [founded, setFounded] = useState('');
  const [website, setWebsite] = useState('');
  const [shopUrl, setShopUrl] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [email, setEmail] = useState('');
  const [timezone, setTimezone] = useState('');
  
  // Collections state
  const [collections, setCollections] = useState<BrandCollection[]>([]);
  const [showNewCollectionModal, setShowNewCollectionModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState<BrandCollection | null>(null);
  const [deletingCollection, setDeletingCollection] = useState<BrandCollection | null>(null);
  
  // Products state
  const [products, setProducts] = useState<BrandProduct[]>([]);
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<BrandProduct | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<BrandProduct | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  
  // Announcements state
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [showNewAnnouncementModal, setShowNewAnnouncementModal] = useState(false);
  const [_editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  
  // File input refs
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Helper to safely convert Firestore Timestamp or string to Date
  const toSafeDate = (value: any): Date => {
    if (!value) return new Date();
    if (typeof value === 'string') return new Date(value);
    if (value.toDate && typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    return new Date();
  };

  // Load brand data
  useEffect(() => {
    async function loadBrand() {
      if (!slug) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const brandData = await getBrandBySlug(slug);
        
        if (!brandData) {
          setError('Brand not found');
          return;
        }
        
        // Check ownership - allow UID match, ownerId match, ownerEmail match, or verified domain email match
        const isOwner = 
          brandData.claimedBy === currentUser?.uid || 
          brandData.ownerId === currentUser?.uid ||
          brandData.ownerEmail?.toLowerCase() === currentUser?.email?.toLowerCase() ||
          brandData.claimRequest?.domainEmail?.toLowerCase() === currentUser?.email?.toLowerCase();
        
        if (!isOwner) {
          setError('You do not have permission to manage this brand');
          return;
        }
        
        setBrand(brandData);
        
        // Populate form fields
        setLogo(brandData.logo || null);
        setBanner(brandData.banner || null);
        setDescription(brandData.description || '');
        setLocation(brandData.location || '');
        setFounded(brandData.founded || '');
        setWebsite(brandData.website || '');
        setShopUrl(brandData.shopUrl || '');
        setInstagram(brandData.instagram || '');
        setFacebook(brandData.facebook || '');
        setTiktok(brandData.tiktok || '');
        setEmail(brandData.email || '');
        setTimezone(brandData.timezone || '');
        
        // Load collections
        const collectionsData = await getBrandCollections(brandData.id);
        setCollections(collectionsData as BrandCollection[]);

        // Self-heal the denormalized `collectionsCount` if it drifted
        // from the actual count. Handles both stale zeros (from before
        // the increment write was wired) and any race-condition drift.
        // Best-effort — a failure here is not user-visible.
        const actualCount = collectionsData.length;
        const storedCount = typeof brandData.collectionsCount === 'number' ? brandData.collectionsCount : 0;
        if (storedCount !== actualCount) {
          try {
            await updateDoc(doc(db, 'brands', brandData.id), {
              collectionsCount: actualCount,
              updatedAt: serverTimestamp(),
            });
          } catch (err) {
            console.warn('collectionsCount self-heal failed:', err);
          }
        }
        
        // Load products
        const productsRef = collection(db, 'brands', brandData.id, 'items');
        const productsQuery = query(productsRef, orderBy('createdAt', 'desc'));
        const productsSnapshot = await getDocs(productsQuery);
        const productsData = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as BrandProduct[];
        setProducts(productsData);
        
        // Load announcements
        const announcementsRef = collection(db, 'brands', brandData.id, 'announcements');
        const announcementsQuery = query(announcementsRef, orderBy('createdAt', 'desc'));
        const announcementsSnapshot = await getDocs(announcementsQuery);
        const announcementsData = announcementsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: toSafeDate(doc.data().createdAt)
        })) as Announcement[];
        setAnnouncements(announcementsData);
        
      } catch (err) {
        console.error('Error loading brand:', err);
        setError('Failed to load brand data');
      } finally {
        setLoading(false);
      }
    }
    
    loadBrand();
  }, [slug, currentUser?.uid]);

  // Show success message temporarily
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Handle logo upload
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !brand) return;

    setUploadingLogo(true);
    try {
      // Compress image (handles HEIC conversion too)
      const compressedFile = await compressImage(file);
      const storageRef = ref(storage, `brands/${brand.id}/logo-${Date.now()}`);
      // Filename includes Date.now(), so each uploaded logo gets a permanent
      // URL — long-cache for CDN + browser. New uploads write to a new URL,
      // so we never serve stale bytes.
      await uploadBytes(storageRef, compressedFile, {
        cacheControl: 'public, max-age=31536000, immutable',
        contentType: 'image/jpeg',
      });
      const url = await getDownloadURL(storageRef);

      // Update Firestore
      await updateDoc(doc(db, 'brands', brand.id), {
        logo: url,
        updatedAt: serverTimestamp()
      });

      setLogo(url);
      showSuccess('Logo uploaded successfully!');
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle banner upload
  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !brand) return;

    setUploadingBanner(true);
    try {
      // Compress image (handles HEIC conversion too)
      const compressedFile = await compressImage(file);
      const storageRef = ref(storage, `brands/${brand.id}/banner-${Date.now()}`);
      // Same content-addressable pattern as the logo above — permanent URL,
      // long-cache it.
      await uploadBytes(storageRef, compressedFile, {
        cacheControl: 'public, max-age=31536000, immutable',
        contentType: 'image/jpeg',
      });
      const url = await getDownloadURL(storageRef);
      
      // Update Firestore
      await updateDoc(doc(db, 'brands', brand.id), {
        banner: url,
        updatedAt: serverTimestamp()
      });
      
      setBanner(url);
      showSuccess('Banner uploaded successfully!');
    } catch (err) {
      console.error('Error uploading banner:', err);
      setError('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  // Remove logo
  const handleRemoveLogo = async () => {
    if (!brand) return;
    
    try {
      await updateDoc(doc(db, 'brands', brand.id), {
        logo: null,
        updatedAt: serverTimestamp()
      });
      setLogo(null);
      showSuccess('Logo removed');
    } catch (err) {
      console.error('Error removing logo:', err);
    }
  };

  // Remove banner
  const handleRemoveBanner = async () => {
    if (!brand) return;
    
    try {
      await updateDoc(doc(db, 'brands', brand.id), {
        banner: null,
        updatedAt: serverTimestamp()
      });
      setBanner(null);
      showSuccess('Banner removed');
    } catch (err) {
      console.error('Error removing banner:', err);
    }
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    if (!brand) return;
    
    setSaving(true);
    try {
      await updateDoc(doc(db, 'brands', brand.id), {
        description: description || null,
        location: location || null,
        founded: founded || null,
        website: website || null,
        shopUrl: shopUrl || null,
        instagram: instagram || null,
        facebook: facebook || null,
        tiktok: tiktok || null,
        email: email || null,
        timezone: timezone || null,
        updatedAt: serverTimestamp()
      });
      
      showSuccess('Profile saved successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  // Delete collection handler
  const handleDeleteCollection = async (collectionToDelete: BrandCollection) => {
    if (!brand) return;
    
    try {
      // Check if there are products in this collection
      const productsInCollection = products.filter(p => p.collectionId === collectionToDelete.id);
      
      // Delete the collection document
      await deleteDoc(doc(db, 'brands', brand.id, 'collections', collectionToDelete.id));

      // Decrement the denormalized count on the brand doc to match.
      // Best-effort; log and continue if the write fails.
      try {
        await updateDoc(doc(db, 'brands', brand.id), {
          collectionsCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.warn('collectionsCount decrement failed:', err);
      }

      // Update products that were in this collection to have no collection
      for (const product of productsInCollection) {
        await updateDoc(doc(db, 'brands', brand.id, 'items', product.id), {
          collectionId: null,
          collectionName: null
        });
      }
      
      // Update local state
      setCollections(prev => prev.filter(c => c.id !== collectionToDelete.id));
      setProducts(prev => prev.map(p => 
        p.collectionId === collectionToDelete.id 
          ? { ...p, collectionId: null, collectionName: null }
          : p
      ));
      setDeletingCollection(null);
      showSuccess('Collection deleted successfully!');
    } catch (err) {
      console.error('Error deleting collection:', err);
      setError('Failed to delete collection');
    }
  };

  // Delete product handler
  const handleDeleteProduct = async (productToDelete: BrandProduct) => {
    if (!brand) return;
    
    try {
      // Delete the product document
      await deleteDoc(doc(db, 'brands', brand.id, 'items', productToDelete.id));
      

      
      // Update local state
      setProducts(prev => prev.filter(p => p.id !== productToDelete.id));
      setDeletingProduct(null);
      showSuccess('Product deleted successfully!');
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product');
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productId: string) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Select/deselect all products
  const toggleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  // Bulk assign products to collection
  const handleBulkAssign = async (collectionId: string | null) => {
    if (!brand || selectedProducts.size === 0) return;
    
    try {
      const selectedCollection = collections.find(c => c.id === collectionId);
      
      // Update each selected product
      for (const productId of selectedProducts) {
        await updateDoc(doc(db, 'brands', brand.id, 'items', productId), {
          collectionId: collectionId || null,
          collectionName: selectedCollection?.name || null,
          updatedAt: serverTimestamp()
        });
      }
      
      // Update local state
      setProducts(prev => prev.map(p => 
        selectedProducts.has(p.id)
          ? { ...p, collectionId: collectionId || null, collectionName: selectedCollection?.name || null }
          : p
      ));
      
      setSelectedProducts(new Set());
      setShowBulkAssignModal(false);
      showSuccess(`${selectedProducts.size} product${selectedProducts.size > 1 ? 's' : ''} updated!`);
    } catch (err) {
      console.error('Error bulk assigning products:', err);
      setError('Failed to update products');
    }
  };

  // Tab navigation
  const tabs: { id: ManageTab; label: string; icon: React.ElementType }[] = [
    { id: 'branding', label: 'Branding', icon: Image },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'collections', label: 'Collections', icon: Sparkles },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'brand-tools', label: 'Brand Tools', icon: Tag },
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-on-bg mb-2 font-heading">Access Denied</h1>
        <p className="text-on-bg mb-4 font-body">{error || 'Unable to load brand management page.'}</p>
        <Link to="/brands" className="text-purple-400 font-medium hover:underline font-body">
          ← Back to Brands
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="glass-card rounded-none border-x-0 border-t-0 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <Link
                to={`/brands/${slug}`}
                className="flex items-center gap-2 text-glass-secondary hover:text-glass transition-default"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="sm:inline">Back</span>
                <span className="hidden sm:inline">to Brand Page</span>
              </Link>
              <div className="hidden sm:block h-6 w-px bg-slate-600"></div>
              <h1 className="text-lg sm:text-xl font-bold text-glass font-body">
                Manage {brand.name}
              </h1>
            </div>

            <a
              href={`/brands/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-glass-secondary hover:text-glass text-sm transition-default"
            >
              <ExternalLink className="w-4 h-4" />
              View Live Page
            </a>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="max-w-6xl mx-auto px-4 mt-4">
          <div className="alert-glass-emerald flex items-center gap-2">
            <Check className="w-5 h-5" />
            {successMessage}
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-6xl mx-auto px-4 mt-4">
          <div className="alert-glass-red flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-300 hover:text-red-200">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Mobile Tab Selector - only visible on small screens */}
        <div className="md:hidden mb-4">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as typeof activeTab)}
            className="w-full px-4 py-3 bg-slate-800/70 border border-slate-600 rounded-xl text-glass font-body focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {tabs.map(tab => (
              <option key={tab.id} value={tab.id}>{tab.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar Navigation - hidden on mobile */}
          <nav className="hidden md:block w-64 flex-shrink-0">
            <div className="glass-card overflow-hidden">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-default font-body ${
                    activeTab === tab.id
                      ? 'bg-purple-500/20 text-purple-300 border-l-4 border-purple-500'
                      : 'text-glass-secondary hover:bg-slate-700/50 border-l-4 border-transparent'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Storage Usage Card */}
            <div className="glass-card p-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <HardDrive className="w-4 h-4 text-glass-muted" />
                <span className="text-sm font-medium text-glass font-body">Storage</span>
              </div>
              
              {(() => {
                const storageUsed = brand.storageUsed || 0;
                const percentUsed = (storageUsed / BRAND_STORAGE_LIMIT) * 100;
                const isOverLimit = storageUsed > BRAND_STORAGE_LIMIT;
                const isNearLimit = storageUsed > BRAND_STORAGE_WARNING;

                return (
                  <>
                    {/* Progress bar */}
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          isOverLimit ? 'bg-red-500' :
                          isNearLimit ? 'bg-amber-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${Math.min(percentUsed, 100)}%` }}
                      />
                    </div>

                    {/* Usage text */}
                    <p className={`text-xs font-body ${
                      isOverLimit ? 'text-red-400' :
                      isNearLimit ? 'text-amber-400' : 'text-glass-muted'
                    }`}>
                      {(storageUsed / (1024 * 1024)).toFixed(1)} MB of 500 MB
                    </p>

                    {/* Warning message */}
                    {isOverLimit && (
                      <p className="text-xs text-red-400 mt-1 font-body">
                        Storage limit exceeded. Contact support to upgrade.
                      </p>
                    )}
                    {isNearLimit && !isOverLimit && (
                      <p className="text-xs text-amber-400 mt-1 font-body">
                        Approaching limit. Consider upgrading soon.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-grow">
            {/* Branding Tab — visual identity only. Brand name
                renaming moved to the Brand Tools tab where the other
                data-cleanup actions live. */}
            {activeTab === 'branding' && (
              <div className="space-y-6">
                {/* Logo Section */}
                <div className="glass-card p-6">
                  <h2 className="text-lg font-semibold text-glass mb-4 font-body">Brand Logo</h2>
                  <p className="text-sm text-glass-secondary mb-4 font-body">
                    Upload a square logo (recommended: 512x512px). This appears on your brand page and in search results.
                  </p>

                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                    <div className="flex-shrink-0">
                      {logo ? (
                        <img
                          src={logo}
                          alt="Brand logo"
                          className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl object-cover border-2 border-slate-600"
                        />
                      ) : (
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl bg-slate-700/50 flex items-center justify-center border-2 border-dashed border-slate-500">
                          <span className="text-3xl sm:text-4xl font-bold text-glass-muted">
                            {brand.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-default font-body"
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </button>
                      {logo && (
                        <button
                          onClick={handleRemoveLogo}
                          className="flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-default font-body"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove Logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Banner Section */}
                <div className="glass-card p-6">
                  <h2 className="text-lg font-semibold text-glass mb-4 font-body">Banner Image</h2>
                  <p className="text-sm text-glass-secondary mb-4 font-body">
                    Upload a wide banner image (recommended: 1920x400px). This appears at the top of your brand page.
                  </p>

                  <div className="space-y-4">
                    {banner ? (
                      <div className="relative">
                        <img
                          src={banner}
                          alt="Brand banner"
                          className="w-full h-48 object-cover rounded-xl border border-slate-600"
                        />
                        <button
                          onClick={handleRemoveBanner}
                          className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-full h-48 rounded-xl bg-slate-700/50 flex items-center justify-center border-2 border-dashed border-slate-500">
                        <div className="text-center">
                          <Image className="w-12 h-12 text-glass-muted mx-auto mb-2" />
                          <p className="text-glass-muted font-body">No banner uploaded</p>
                        </div>
                      </div>
                    )}

                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES}
                      onChange={handleBannerUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => bannerInputRef.current?.click()}
                      disabled={uploadingBanner}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-default font-body"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-glass font-body">Brand Profile</h2>
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-default font-body"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="textarea-glass"
                      placeholder="Tell collectors about your brand..."
                    />
                  </div>

                  {/* Location & Founded */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        Location
                      </label>
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="input-glass"
                        placeholder="e.g., Denver, CO"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        Founded
                      </label>
                      <input
                        type="text"
                        value={founded}
                        onChange={(e) => setFounded(e.target.value)}
                        className="input-glass"
                        placeholder="e.g., 2018"
                      />
                    </div>
                  </div>

                  {/* Website & Shop */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
                        <Globe className="w-4 h-4 inline mr-1" />
                        Website
                      </label>
                      <input
                        type="url"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className="input-glass"
                        placeholder="https://yourbrand.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
                        <ExternalLink className="w-4 h-4 inline mr-1" />
                        Shop URL
                      </label>
                      <input
                        type="url"
                        value={shopUrl}
                        onChange={(e) => setShopUrl(e.target.value)}
                        className="input-glass"
                        placeholder="https://shop.yourbrand.com"
                      />
                    </div>
                  </div>

                  {/* Social Media */}
                  <div>
                    <h3 className="text-sm font-medium text-glass-secondary mb-3 font-body">Social Media</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-glass-muted mb-1 font-body">
                          <Instagram className="w-3 h-3 inline mr-1" />
                          Instagram
                        </label>
                        <input
                          type="text"
                          value={instagram}
                          onChange={(e) => setInstagram(e.target.value)}
                          className="input-glass"
                          placeholder="@yourbrand"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-glass-muted mb-1 font-body">
                          Facebook
                        </label>
                        <input
                          type="text"
                          value={facebook}
                          onChange={(e) => setFacebook(e.target.value)}
                          className="input-glass"
                          placeholder="facebook.com/yourbrand"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-glass-muted mb-1 font-body">
                          TikTok
                        </label>
                        <input
                          type="text"
                          value={tiktok}
                          onChange={(e) => setTiktok(e.target.value)}
                          className="input-glass"
                          placeholder="@yourbrand"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-glass-muted mb-1 font-body">
                          Contact Email
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="input-glass"
                          placeholder="hello@yourbrand.com"
                        />
                      </div>

                      {/* Timezone */}
                      <div className="col-span-2">
                        <label className="block text-xs text-glass-muted mb-1 font-body">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Default Timezone
                        </label>
                        <p className="text-xs text-glass-muted mb-2 font-body">
                          Used as the default for events you create
                        </p>
                        <div className="flex items-center gap-2">
                          <select
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            className="select-glass flex-grow"
                          >
                            <option value="">Select timezone...</option>
                            <optgroup label="🇺🇸 United States">
                              {US_TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>
                                  {tz.label}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="🌍 International">
                              {INTERNATIONAL_TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>
                                  {tz.label}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                          <button
                            type="button"
                            onClick={() => setTimezone(getUserTimezone())}
                            className="px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-glass rounded-lg transition-default whitespace-nowrap font-body"
                          >
                            Detect
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Collections Tab */}
            {activeTab === 'collections' && (
              <div className="space-y-6">
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-glass font-body">Official Collections</h2>
                      <p className="text-sm text-glass-secondary font-body">
                        Create collections to organize your releases. Collectors can browse and add items directly.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowNewCollectionModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-body"
                    >
                      <Plus className="w-4 h-4" />
                      New Collection
                    </button>
                  </div>

                  {collections.length === 0 ? (
                    <div className="text-center py-12 bg-slate-800/40 rounded-xl border border-slate-600">
                      <Sparkles className="w-12 h-12 text-glass-muted mx-auto mb-3" />
                      <p className="text-glass font-medium font-body">No collections yet</p>
                      <p className="text-sm text-glass-secondary mb-4 font-body">
                        Create your first collection to showcase your releases
                      </p>
                      <button
                        onClick={() => setShowNewCollectionModal(true)}
                        className="text-purple-400 font-medium hover:underline font-body"
                      >
                        + Create Collection
                      </button>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {collections.map(collectionItem => (
                        <div
                          key={collectionItem.id}
                          className="flex items-center gap-4 p-4 bg-slate-800/40 rounded-xl hover:bg-slate-700/50 transition-default border border-slate-600"
                        >
                          {/* Cover Image */}
                          {collectionItem.coverImage ? (
                            <img
                              src={collectionItem.coverImage}
                              alt={collectionItem.name}
                              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Sparkles className="w-8 h-8 text-purple-400" />
                            </div>
                          )}

                          {/* Collection Info */}
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-glass truncate font-body">{collectionItem.name}</h3>
                              {collectionItem.releaseType && collectionItem.releaseType !== 'collection' && (
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium">
                                  {collectionItem.releaseType}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-glass-secondary font-body">
                              {products.filter(p => p.collectionId === collectionItem.id).length} items
                              {collectionItem.releaseDate && ` • ${toSafeDate(collectionItem.releaseDate).toLocaleDateString()}`}
                              {!collectionItem.releaseDate && collectionItem.releaseSeason && collectionItem.releaseYear && ` • ${collectionItem.releaseSeason} ${collectionItem.releaseYear}`}
                            </p>
                            {collectionItem.collabWith && (
                              <p className="text-xs text-purple-400 mt-1 font-body">
                                🤝 Collab with {collectionItem.collabWith}
                              </p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => setEditingCollection(collectionItem)}
                              className="p-2 text-glass-secondary hover:bg-slate-700 hover:text-purple-400 rounded-lg transition-default"
                              title="Edit collection"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeletingCollection(collectionItem)}
                              className="p-2 text-glass-muted hover:bg-slate-700 hover:text-red-400 rounded-lg transition-default"
                              title="Delete collection"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-glass font-body">Products</h2>
                    <p className="text-sm text-glass-secondary font-body">
                      Add products to your collections. These become the official catalog that collectors can browse.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNewProductModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-body"
                  >
                    <Plus className="w-4 h-4" />
                    Add Product
                  </button>
                </div>

                {products.length === 0 ? (
                  <div className="text-center py-12 bg-slate-800/40 rounded-xl border border-slate-600">
                    <Package className="w-12 h-12 text-glass-muted mx-auto mb-3" />
                    <p className="text-glass font-medium font-body">No products yet</p>
                    <p className="text-sm text-glass-secondary mb-4 font-body">
                      Add your first product to start building your catalog
                    </p>
                    <button
                      onClick={() => setShowNewProductModal(true)}
                      className="text-purple-400 font-medium hover:underline font-body"
                    >
                      + Add Product
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Bulk Action Bar */}
                    <div className="flex items-center justify-between p-3 bg-slate-800/40 rounded-lg border border-slate-600">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedProducts.size === products.length && products.length > 0}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-purple-500 focus:ring-purple-500"
                          />
                          <span className="text-sm text-glass-secondary font-body">
                            {selectedProducts.size === 0
                              ? 'Select all'
                              : `${selectedProducts.size} selected`}
                          </span>
                        </label>

                        {selectedProducts.size > 0 && (
                          <button
                            onClick={() => setSelectedProducts(new Set())}
                            className="text-sm text-glass-muted hover:text-glass-secondary font-body"
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      {selectedProducts.size > 0 && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowBulkAssignModal(true)}
                            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-body"
                          >
                            Assign to Collection
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Filter by collection */}
                    <div className="flex gap-2 flex-wrap mb-4">
                      <span className="text-sm text-glass-muted py-1 font-body">Filter:</span>
                      <button
                        className="px-3 py-1 text-sm rounded-full bg-purple-500/20 text-purple-300 font-body"
                      >
                        All ({products.length})
                      </button>
                      {collections.map(col => {
                        const count = products.filter(p => p.collectionId === col.id).length;
                        if (count === 0) return null;
                        return (
                          <button
                            key={col.id}
                            className="px-3 py-1 text-sm rounded-full bg-slate-700/50 text-glass-secondary hover:bg-slate-600/50 font-body"
                          >
                            {col.name} ({count})
                          </button>
                        );
                      })}
                      {(() => {
                        const unassigned = products.filter(p => !p.collectionId).length;
                        if (unassigned === 0) return null;
                        return (
                          <button
                            className="px-3 py-1 text-sm rounded-full bg-slate-700/50 text-glass-secondary hover:bg-slate-600/50 font-body"
                          >
                            Unassigned ({unassigned})
                          </button>
                        );
                      })()}
                    </div>
                    
                    {/* Products grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {products.map(product => (
                        <div
                          key={product.id}
                          className={`rounded-xl overflow-hidden transition-all border ${
                            selectedProducts.has(product.id)
                              ? 'border-purple-500 ring-2 ring-purple-500/30'
                              : 'border-slate-600 hover:border-slate-500'
                          } bg-slate-800/40`}
                        >
                          {/* Product image with checkbox */}
                          <div className="relative">
                            {product.photos && product.photos.length > 0 && getPhotoUrl(product.photos[0]) ? (
                              <img
                                src={getPhotoUrl(product.photos[0])}
                                alt={product.name}
                                className="w-full h-40 object-cover"
                              />
                            ) : (
                              <div className="w-full h-40 bg-slate-700/50 flex items-center justify-center">
                                <Package className="w-12 h-12 text-glass-muted" />
                              </div>
                            )}

                            {/* Selection checkbox */}
                            <label className="absolute top-2 left-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedProducts.has(product.id)}
                                onChange={() => toggleProductSelection(product.id)}
                                className="w-5 h-5 rounded border-2 border-white bg-white/80 text-purple-600 focus:ring-purple-500 focus:ring-offset-0 shadow-sm"
                              />
                            </label>

                            {/* Photo credit overlay */}
                            {product.photos && product.photos.length > 0 && getPhotoCreditName(product.photos[0]) && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 pt-4">
                                <span className="text-xs text-white/90">📷 {getPhotoCreditName(product.photos[0])}</span>
                              </div>
                            )}
                          </div>

                          {/* Photo caption */}
                          {product.photos && product.photos.length > 0 && getPhotoCaption(product.photos[0]) && (
                            <div className="px-4 py-1.5 bg-slate-700/30 border-b border-slate-600">
                              <p className="text-xs text-glass-muted italic font-body">{getPhotoCaption(product.photos[0])}</p>
                            </div>
                          )}

                          {/* Product info */}
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <h3 className="font-semibold text-glass truncate font-body">{product.name}</h3>
                                {product.collectionName && (
                                  <p className="text-xs text-purple-400 truncate font-body">{product.collectionName}</p>
                                )}
                              </div>
                              {/* Status badge */}
                              <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${
                                product.status === 'available' ? 'bg-green-500/20 text-green-400' :
                                product.status === 'coming_soon' ? 'bg-blue-500/20 text-blue-400' :
                                product.status === 'sold_out' ? 'bg-red-500/20 text-red-400' :
                                'bg-slate-600 text-glass-muted'
                              }`}>
                                {product.status === 'available' ? '✅' :
                                 product.status === 'coming_soon' ? '🔜' :
                                 product.status === 'sold_out' ? '🚫' : '⏹️'}
                              </span>
                            </div>

                            {product.colorDescription && (
                              <p className="text-sm text-glass-secondary mt-1 line-clamp-2 font-body">{product.colorDescription}</p>
                            )}

                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-600">
                              {product.price ? (
                                <span className="text-sm font-medium text-glass font-body">${product.price.toFixed(2)}</span>
                              ) : (
                                <span className="text-sm text-glass-muted font-body">No price</span>
                              )}

                              <div className="flex gap-1">
                                <button
                                  onClick={() => setEditingProduct(product)}
                                  className="p-1.5 text-glass-secondary hover:bg-slate-700 rounded transition-default"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setDeletingProduct(product)}
                                  className="p-1.5 text-glass-muted hover:text-red-400 hover:bg-red-500/20 rounded transition-default"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Brand Tools tab — brand name rename + collection
                normalizer + per-polish rename/merge/tag editing.
                Separate component to keep this file readable. */}
            {activeTab === 'brand-tools' && brand && (
              <BrandToolsTab
                brand={brand}
                onBrandRenamed={(newName) => {
                  // Reflect the rename in local state so downstream
                  // tabs (Products, etc.) see the new name without a
                  // full page reload.
                  setBrand((prev) => (prev ? { ...prev, name: newName } : prev));
                }}
              />
            )}

            {/* Announcements Tab */}
            {activeTab === 'announcements' && (
              <div className="space-y-6">
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-glass font-body">Announcements</h2>
                      <p className="text-sm text-glass-secondary font-body">
                        Post news, release announcements, and updates for your followers.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowNewAnnouncementModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-body"
                    >
                      <Plus className="w-4 h-4" />
                      New Announcement
                    </button>
                  </div>

                  {announcements.length === 0 ? (
                    <div className="text-center py-12 bg-slate-800/40 rounded-xl border border-slate-600">
                      <Megaphone className="w-12 h-12 text-glass-muted mx-auto mb-3" />
                      <p className="text-glass font-medium font-body">No announcements yet</p>
                      <p className="text-sm text-glass-secondary mb-4 font-body">
                        Keep your followers updated with news and releases
                      </p>
                      <button
                        onClick={() => setShowNewAnnouncementModal(true)}
                        className="text-purple-400 font-medium hover:underline font-body"
                      >
                        + Post Announcement
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {announcements.map(announcement => (
                        <div
                          key={announcement.id}
                          className="p-4 bg-slate-800/40 rounded-xl border border-slate-600"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  announcement.type === 'release' ? 'bg-purple-500/20 text-purple-300' :
                                  announcement.type === 'sale' ? 'bg-green-500/20 text-green-400' :
                                  announcement.type === 'event' ? 'bg-blue-500/20 text-blue-400' :
                                  'bg-slate-600 text-glass-muted'
                                }`}>
                                  {announcement.type}
                                </span>
                                {announcement.isPinned && (
                                  <span className="text-xs text-amber-400">📌 Pinned</span>
                                )}
                              </div>
                              <h3 className="font-semibold text-glass font-body">{announcement.title}</h3>
                              <p className="text-sm text-glass-secondary mt-1 font-body">{announcement.content}</p>
                              <p className="text-xs text-glass-muted mt-2 font-body">
                                {announcement.createdAt.toLocaleDateString()}
                              </p>
                            </div>
                            <button
                              onClick={() => setEditingAnnouncement(announcement)}
                              className="p-2 text-glass-secondary hover:bg-slate-700 rounded-lg transition-default"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* New/Edit Collection Modal */}
      {(showNewCollectionModal || editingCollection) && brand && (
        <BrandCollectionModal
          brandId={brand.id}
          brandName={brand.name}
          collection={editingCollection}
          onClose={() => {
            setShowNewCollectionModal(false);
            setEditingCollection(null);
          }}
          onSave={(savedCollection) => {
            if (editingCollection) {
              // Update existing collection in state
              setCollections(prev => 
                prev.map(c => c.id === savedCollection.id ? savedCollection : c)
              );
            } else {
              // Add new collection to state
              setCollections(prev => [...prev, savedCollection]);
            }
            setShowNewCollectionModal(false);
            setEditingCollection(null);
          }}
        />
      )}

      {/* New/Edit Product Modal */}
      {(showNewProductModal || editingProduct) && brand && (
        <BrandProductModal
          brandId={brand.id}
          brandName={brand.name}
          collections={collections}
          product={editingProduct}
          onClose={() => {
            setShowNewProductModal(false);
            setEditingProduct(null);
          }}
          onSave={(savedProduct) => {
            const product = savedProduct as unknown as BrandProduct;
            if (editingProduct) {
              // Update existing product in state
              setProducts(prev =>
                prev.map(p => p.id === product.id ? product : p)
              );
            } else {
              // Add new product to state
              setProducts(prev => [product, ...prev]);
            }
            setShowNewProductModal(false);
            setEditingProduct(null);
          }}
        />
      )}

      {/* New Announcement Modal - Placeholder */}
      {showNewAnnouncementModal && (
        <div className="modal-overlay">
          <div className="glass-modal max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-glass font-body">New Announcement</h2>
              <button onClick={() => setShowNewAnnouncementModal(false)} className="text-glass-muted hover:text-glass">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-glass-secondary mb-4 font-body">Announcement creation form coming soon!</p>
            <button
              onClick={() => setShowNewAnnouncementModal(false)}
              className="w-full py-2 bg-slate-700 text-glass rounded-lg hover:bg-slate-600 transition-default font-body"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Collection Confirmation Modal */}
      {deletingCollection && (
        <div className="modal-overlay">
          <div className="glass-modal max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-glass font-body">Delete Collection</h2>
                <p className="text-sm text-glass-secondary font-body">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-glass-secondary mb-2 font-body">
              Are you sure you want to delete <strong className="text-glass">"{deletingCollection.name}"</strong>?
            </p>

            {(() => {
              const productsInCollection = products.filter(p => p.collectionId === deletingCollection.id).length;
              if (productsInCollection > 0) {
                return (
                  <p className="text-amber-300 text-sm mb-4 p-3 bg-amber-500/20 rounded-lg border border-amber-500/30 font-body">
                    ⚠️ This collection contains {productsInCollection} product{productsInCollection > 1 ? 's' : ''}.
                    They will be moved to "Unassigned" but won't be deleted.
                  </p>
                );
              }
              return null;
            })()}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setDeletingCollection(null)}
                className="flex-1 px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700 font-medium transition-default font-body"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCollection(deletingCollection)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-default font-body"
              >
                Delete Collection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Product Confirmation Modal */}
      {deletingProduct && (
        <div className="modal-overlay">
          <div className="glass-modal max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-glass font-body">Delete Product</h2>
                <p className="text-sm text-glass-secondary font-body">This action cannot be undone</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-800/40 rounded-lg border border-slate-600">
              {deletingProduct.photos && deletingProduct.photos.length > 0 && getPhotoUrl(deletingProduct.photos[0]) ? (
                <img
                  src={getPhotoUrl(deletingProduct.photos[0])}
                  alt={deletingProduct.name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center">
                  <Package className="w-8 h-8 text-glass-muted" />
                </div>
              )}
              <div>
                <p className="font-medium text-glass font-body">{deletingProduct.name}</p>
                {deletingProduct.collectionName && (
                  <p className="text-sm text-purple-400 font-body">{deletingProduct.collectionName}</p>
                )}
              </div>
            </div>

            <p className="text-glass-secondary text-sm font-body">
              Are you sure you want to delete this product? This will remove it from your catalog permanently.
            </p>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setDeletingProduct(null)}
                className="flex-1 px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700 font-medium transition-default font-body"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteProduct(deletingProduct)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-default font-body"
              >
                Delete Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign to Collection Modal */}
      {showBulkAssignModal && (
        <div className="modal-overlay">
          <div className="glass-modal max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-500/20 rounded-full">
                <Sparkles className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-glass font-body">Assign to Collection</h2>
                <p className="text-sm text-glass-secondary font-body">{selectedProducts.size} product{selectedProducts.size > 1 ? 's' : ''} selected</p>
              </div>
            </div>

            <p className="text-glass-secondary mb-4 font-body">
              Choose a collection to assign the selected products to:
            </p>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {/* No collection option */}
              <button
                onClick={() => handleBulkAssign(null)}
                className="w-full p-3 text-left rounded-lg border border-slate-600 hover:bg-slate-700/50 transition-default"
              >
                <span className="font-medium text-glass-secondary font-body">📦 No Collection (Unassigned)</span>
              </button>

              {/* Existing collections */}
              {collections.map(col => (
                <button
                  key={col.id}
                  onClick={() => handleBulkAssign(col.id)}
                  className="w-full p-3 text-left rounded-lg border border-slate-600 hover:bg-purple-500/20 hover:border-purple-500/50 transition-default"
                >
                  <div className="flex items-center gap-3">
                    {col.coverImage ? (
                      <img src={col.coverImage} alt={col.name} className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-purple-500/20 rounded flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-glass font-body">{col.name}</span>
                      {col.releaseType && col.releaseType !== 'collection' && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full">
                          {col.releaseType}
                        </span>
                      )}
                      <p className="text-xs text-glass-muted font-body">
                        {products.filter(p => p.collectionId === col.id).length} products
                      </p>
                    </div>
                  </div>
                </button>
              ))}

              {collections.length === 0 && (
                <p className="text-center text-glass-muted py-4 font-body">
                  No collections yet. Create one in the Collections tab first.
                </p>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowBulkAssignModal(false)}
                className="w-full px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700 font-medium transition-default font-body"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
